import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ShieldCheck, Clock, Sparkles } from 'lucide-react';
import { COMPANY } from '../data/site';
import { EASE } from './Reveal';

const HERO_IMG = 'https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: '115%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 900], [0, 140]);
  const bgY = useTransform(scrollY, [0, 900], [0, -80]);

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.querySelector('#kontakt');
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="start" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden bg-navy">
      <motion.div style={{ y: bgY }} className="fm-grid-bg pointer-events-none absolute inset-0" />
      <div className="fm-glow pointer-events-none absolute -left-40 top-1/3 h-[560px] w-[560px]" />
      <div className="fm-glow pointer-events-none absolute -right-32 -top-24 h-[480px] w-[480px] opacity-70" />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 pb-24 pt-36 sm:px-6 lg:grid-cols-12 lg:px-8 lg:pt-28">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-brand" />
            <span className="text-xs font-semibold tracking-[0.28em] uppercase text-slate-200">
              {COMPANY.tagline}
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[17vw] font-extrabold leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-[6.5rem]"
          >
            <MaskedLine delay={0.3}>Wir packen</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-brand">das.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subline"
          >
            Ihr zuverlässiger Partner für Umzüge, Möbeltransporte und Entrümpelungen
            in Berlin & Brandenburg.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href="#kontakt"
              onClick={scrollToContact}
              data-testid="hero-cta-quote"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-bold text-white shadow-xl shadow-brand/40 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-hover hover:shadow-brand/60"
            >
              Kostenloses Angebot anfragen
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400"
          >
            {[
              { icon: ShieldCheck, label: 'Festpreis-Angebote' },
              { icon: Clock, label: 'Termingerecht' },
              { icon: Sparkles, label: 'Besenreine Übergabe' },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-brand" /> {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-black/50">
              <img
                src={HERO_IMG}
                alt="Umzugsteam von F&M beim Möbeltransport"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
              className="absolute -left-10 bottom-10 rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-xl"
              data-testid="hero-card"
            >
              <p className="font-display text-3xl font-extrabold text-white">24h</p>
              <p className="mt-1 text-xs font-medium tracking-wide text-slate-300">
                Rückmeldung auf<br />Ihre Anfrage
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
      >
        <div className="flex h-12 w-7 items-start justify-center rounded-full border border-white/25 p-1.5">
          <motion.span
            animate={{ y: [0, 16, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
            className="h-2 w-2 rounded-full bg-brand"
          />
        </div>
      </motion.div>
    </section>
  );
}
